import React from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  InputAdornment,
  Chip,
  Divider,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { NumberInput } from "lib/components/ui/number-input/number-input.component";
import {
  CreditCard,
  Person,
  MonetizationOn,
  Save,
  Info,
  Storage,
  AccessTime,
  CheckCircle,
} from "@mui/icons-material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  useReadPaymentSettingsQuery,
  useUpdatePaymentSettingsMutation,
} from "api/endpoints/settings";
import { toast } from "react-toastify";

const priceFieldValidation = Yup.string().test(
  "is-valid-price",
  "قیمت باید عدد معتبر باشد",
  function (value) {
    if (!value) return false;
    const valueWithNoSeparator = value.replace(/,/g, "");
    const numericValue = Number(valueWithNoSeparator);
    return !isNaN(numericValue) && numericValue >= 0;
  }
);

const validationSchema = Yup.object({
  cardNumber: Yup.string()
    .test(
      "is-valid-card-number",
      "شماره کارت باید 16 رقم باشد",
      function (value) {
        if (!value) return false;
        // Remove dashes and check if it's 16 digits
        const digitsOnly = value.replace(/-/g, "");
        return /^\d{16}$/.test(digitsOnly);
      }
    )
    .required("شماره کارت الزامی است"),
  cardOwnerName: Yup.string()
    .min(2, "نام صاحب کارت باید حداقل 2 کاراکتر باشد")
    .required("نام صاحب کارت الزامی است"),
  gigLimitTimeUnlimitedPrice: priceFieldValidation.required(
    "قیمت پلن حجم محدود الزامی است"
  ),
  gigUnlimitedTimeLimitPrice: priceFieldValidation.required(
    "قیمت پلن زمان محدود الزامی است"
  ),
  gigLimitTimeLimitPrice: priceFieldValidation.required(
    "قیمت پلن اقتصادی الزامی است"
  ),
  gigLimitTimeUnlimitedEnabled: Yup.boolean(),
  gigUnlimitedTimeLimitEnabled: Yup.boolean(),
  gigLimitTimeLimitEnabled: Yup.boolean(),
});

export const SettingsPaymentComponent = ({ embedded = false }: { embedded?: boolean }) => {
  const { data: settings, isLoading, error } = useReadPaymentSettingsQuery();
  const [updateSettings, { isLoading: updating, error: updateError }] =
    useUpdatePaymentSettingsMutation();

  // Helper function to format number with thousand separators
  const formatNumberWithSeparators = (value: number | string): string => {
    if (!value) return "";
    const numValue = typeof value === "string" ? Number(value) : value;
    return numValue.toLocaleString();
  };

  // Helper function to convert string to number (NumberInput already cleans the input)
  const parseNumberFromString = (value: string): number => {
    const valueWithNoSeparator = value.replace(/,/g, "");
    const numericValue = Number(valueWithNoSeparator);
    return numericValue;
  };

  // Helper function to format card number with dashes
  const formatCardNumber = (value: string): string => {
    // Remove all non-digit characters
    const digitsOnly = value.replace(/\D/g, "");

    // Add dashes every 4 digits
    const formatted = digitsOnly.replace(/(\d{4})(?=\d)/g, "$1-");

    return formatted;
  };

  // Helper function to handle card number input changes
  const handleCardNumberChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    setFieldValue: any
  ) => {
    const inputValue = e.target.value;
    const formattedValue = formatCardNumber(inputValue);

    // Handle the edge case: if user deletes and leaves trailing dash, remove it
    const finalValue =
      formattedValue.endsWith("-") && formattedValue.length > 4
        ? formattedValue.slice(0, -1)
        : formattedValue;

    setFieldValue("cardNumber", finalValue);
  };

  const handleSubmit = async (values: {
    cardNumber: string;
    cardOwnerName: string;
    gigLimitTimeUnlimitedPrice: number;
    gigUnlimitedTimeLimitPrice: number;
    gigLimitTimeLimitPrice: number;
    gigLimitTimeUnlimitedEnabled: boolean;
    gigUnlimitedTimeLimitEnabled: boolean;
    gigLimitTimeLimitEnabled: boolean;
  }) => {
    try {
      await toast.promise(
        updateSettings({
          payload: values,
        }).unwrap(),
        {
          error: {
            render:
              "هنگام بروزرسانی تنظیمات مشکلی بوجود آمد، لطفا با پشتیبانی ارتباط برقرار نمایید.",
          },
          pending: {
            render: "درحال بروزرسانی تنظیمات، لطفا منتظر بمانید...",
            autoClose: 5000,
          },
          success: "تنظیمات با موفقیت بروزرسانی شد.",
        }
      );
    } catch (err) {
      console.error("Failed to update settings:", err);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        خطا در بارگذاری تنظیمات
      </Alert>
    );
  }

  return (
    <Box p={embedded ? 0 : 3}>
      {!embedded && (
        <Typography variant="h4" gutterBottom>
          تنظیمات پرداخت
        </Typography>
      )}

      <Paper sx={{ p: 3 }}>
        {updateError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            خطا در به‌روزرسانی تنظیمات
          </Alert>
        )}

        <Formik
          initialValues={{
            cardNumber: settings?.cardNumber
              ? formatCardNumber(settings.cardNumber)
              : "",
            cardOwnerName: settings?.cardOwnerName || "",
            gigLimitTimeUnlimitedPrice: formatNumberWithSeparators(
              settings?.gigLimitTimeUnlimitedPrice || 0
            ),
            gigUnlimitedTimeLimitPrice: formatNumberWithSeparators(
              settings?.gigUnlimitedTimeLimitPrice || 0
            ),
            gigLimitTimeLimitPrice: formatNumberWithSeparators(
              settings?.gigLimitTimeLimitPrice || 0
            ),
            gigLimitTimeUnlimitedEnabled:
              (settings as any)?.gigLimitTimeUnlimitedEnabled ?? true,
            gigUnlimitedTimeLimitEnabled:
              (settings as any)?.gigUnlimitedTimeLimitEnabled ?? true,
            gigLimitTimeLimitEnabled:
              (settings as any)?.gigLimitTimeLimitEnabled ?? true,
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            const numericValues = {
              cardNumber: values.cardNumber.replace(/-/g, ""), // Remove dashes before sending to API
              cardOwnerName: values.cardOwnerName,
              gigLimitTimeUnlimitedPrice: parseNumberFromString(
                values.gigLimitTimeUnlimitedPrice.toString()
              ),
              gigUnlimitedTimeLimitPrice: parseNumberFromString(
                values.gigUnlimitedTimeLimitPrice.toString()
              ),
              gigLimitTimeLimitPrice: parseNumberFromString(
                values.gigLimitTimeLimitPrice.toString()
              ),
              gigLimitTimeUnlimitedEnabled: values.gigLimitTimeUnlimitedEnabled,
              gigUnlimitedTimeLimitEnabled: values.gigUnlimitedTimeLimitEnabled,
              gigLimitTimeLimitEnabled: values.gigLimitTimeLimitEnabled,
            };
            handleSubmit(numericValues);
          }}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
          }) => (
            <Form>
              <Grid container spacing={3}>
                {/* Card Information */}
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        اطلاعات کارت بانکی
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            name="cardNumber"
                            label="شماره کارت"
                            value={values.cardNumber}
                            onChange={(e) =>
                              handleCardNumberChange(e, setFieldValue)
                            }
                            onBlur={handleBlur}
                            error={
                              touched.cardNumber && Boolean(errors.cardNumber)
                            }
                            helperText={touched.cardNumber && errors.cardNumber}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <CreditCard />
                                </InputAdornment>
                              ),
                            }}
                            placeholder="1234-5678-9012-3456"
                            dir="ltr"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            name="cardOwnerName"
                            label="نام صاحب کارت"
                            value={values.cardOwnerName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.cardOwnerName &&
                              Boolean(errors.cardOwnerName)
                            }
                            helperText={
                              touched.cardOwnerName && errors.cardOwnerName
                            }
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Person />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Pricing */}
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        تعرفه قیمت‌ها
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <NumberInput
                            fullWidth
                            name="gigLimitTimeUnlimitedPrice"
                            label="حجم محدود - زمان نامحدود"
                            value={values.gigLimitTimeUnlimitedPrice}
                            onChange={(e) => {
                              const numericValue = parseNumberFromString(
                                e.target.value
                              );
                              setFieldValue(
                                "gigLimitTimeUnlimitedPrice",
                                formatNumberWithSeparators(numericValue)
                              );
                            }}
                            onBlur={handleBlur}
                            error={
                              touched.gigLimitTimeUnlimitedPrice &&
                              Boolean(errors.gigLimitTimeUnlimitedPrice)
                            }
                            helperText={
                              touched.gigLimitTimeUnlimitedPrice &&
                              errors.gigLimitTimeUnlimitedPrice
                            }
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <MonetizationOn />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  تومان
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <NumberInput
                            fullWidth
                            name="gigUnlimitedTimeLimitPrice"
                            label="حجم نامحدود - زمان محدود"
                            value={values.gigUnlimitedTimeLimitPrice}
                            onChange={(e) => {
                              const numericValue = parseNumberFromString(
                                e.target.value
                              );
                              setFieldValue(
                                "gigUnlimitedTimeLimitPrice",
                                formatNumberWithSeparators(numericValue)
                              );
                            }}
                            onBlur={handleBlur}
                            error={
                              touched.gigUnlimitedTimeLimitPrice &&
                              Boolean(errors.gigUnlimitedTimeLimitPrice)
                            }
                            helperText={
                              touched.gigUnlimitedTimeLimitPrice &&
                              errors.gigUnlimitedTimeLimitPrice
                            }
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <MonetizationOn />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  تومان
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <NumberInput
                            fullWidth
                            name="gigLimitTimeLimitPrice"
                            label="حجم محدود - زمان محدود"
                            value={values.gigLimitTimeLimitPrice}
                            onChange={(e) => {
                              const numericValue = parseNumberFromString(
                                e.target.value
                              );
                              setFieldValue(
                                "gigLimitTimeLimitPrice",
                                formatNumberWithSeparators(numericValue)
                              );
                            }}
                            onBlur={handleBlur}
                            error={
                              touched.gigLimitTimeLimitPrice &&
                              Boolean(errors.gigLimitTimeLimitPrice)
                            }
                            helperText={
                              touched.gigLimitTimeLimitPrice &&
                              errors.gigLimitTimeLimitPrice
                            }
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <MonetizationOn />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  تومان
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Plan Enable/Disable Toggles */}
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        فعال/غیرفعال کردن پلن‌ها
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 3 }}
                      >
                        پلن‌های غیرفعال در صفحه خرید نمایش داده نمی‌شوند
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={values.gigLimitTimeUnlimitedEnabled}
                                onChange={(e) =>
                                  setFieldValue(
                                    "gigLimitTimeUnlimitedEnabled",
                                    e.target.checked
                                  )
                                }
                                color="primary"
                              />
                            }
                            label="حجم محدود - زمان نامحدود"
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={values.gigUnlimitedTimeLimitEnabled}
                                onChange={(e) =>
                                  setFieldValue(
                                    "gigUnlimitedTimeLimitEnabled",
                                    e.target.checked
                                  )
                                }
                                color="primary"
                              />
                            }
                            label="حجم نامحدود - زمان محدود"
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={values.gigLimitTimeLimitEnabled}
                                onChange={(e) =>
                                  setFieldValue(
                                    "gigLimitTimeLimitEnabled",
                                    e.target.checked
                                  )
                                }
                                color="primary"
                              />
                            }
                            label="حجم محدود - زمان محدود"
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Pricing Formula Explanations */}
                <Grid item xs={12}>
                  <Card
                    variant="outlined"
                    sx={{ bgcolor: "info.light", color: "info.contrastText" }}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <Info sx={{ mr: 1 }} />
                        <Typography variant="h6">
                          فرمول محاسبه قیمت نهایی
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ mb: 3 }}>
                        در زیر توضیح می‌دهیم که چگونه قیمت نهایی برای هر پلن
                        محاسبه می‌شود:
                      </Typography>

                      <Grid container spacing={2}>
                        {/* GIG_LIMIT_TIME_UNLIMITED */}
                        <Grid item xs={12} md={4}>
                          <Card
                            sx={{
                              bgcolor: "background.paper",
                              color: "text.primary",
                            }}
                          >
                            <CardContent>
                              <Box display="flex" alignItems="center" mb={2}>
                                <Storage
                                  sx={{ mr: 1, color: "primary.main" }}
                                />
                                <Typography
                                  variant="subtitle1"
                                  fontWeight="bold"
                                >
                                  حجم محدود - زمان نامحدود
                                </Typography>
                              </Box>
                              <Typography variant="body2" sx={{ mb: 2 }}>
                                <strong>فرمول:</strong> قیمت پایه × تعداد
                                گیگابایت انتخاب شده
                              </Typography>
                              <Box
                                sx={{
                                  bgcolor: "grey.100",
                                  p: 2,
                                  borderRadius: 1,
                                  mb: 2,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  fontFamily="monospace"
                                >
                                  مثال: اگر قیمت پایه 1000 تومان باشد و کاربر 50
                                  گیگابایت انتخاب کند:
                                  <br />
                                  1000 × 50 = 50,000 تومان
                                </Typography>
                              </Box>
                              <Chip
                                label="قیمت در هر گیگابایت"
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </CardContent>
                          </Card>
                        </Grid>

                        {/* GIG_UNLIMITED_TIME_LIMIT */}
                        <Grid item xs={12} md={4}>
                          <Card
                            sx={{
                              bgcolor: "background.paper",
                              color: "text.primary",
                            }}
                          >
                            <CardContent>
                              <Box display="flex" alignItems="center" mb={2}>
                                <AccessTime
                                  sx={{ mr: 1, color: "primary.main" }}
                                />
                                <Typography
                                  variant="subtitle1"
                                  fontWeight="bold"
                                >
                                  حجم نامحدود - زمان محدود
                                </Typography>
                              </Box>
                              <Typography variant="body2" sx={{ mb: 2 }}>
                                <strong>فرمول:</strong> قیمت پایه × تعداد ماه
                                انتخاب شده
                              </Typography>
                              <Box
                                sx={{
                                  bgcolor: "grey.100",
                                  p: 2,
                                  borderRadius: 1,
                                  mb: 2,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  fontFamily="monospace"
                                >
                                  مثال: اگر قیمت پایه 5000 تومان باشد و کاربر 3
                                  ماه انتخاب کند:
                                  <br />
                                  5000 × 3 = 15,000 تومان
                                </Typography>
                              </Box>
                              <Chip
                                label="قیمت در هر ماه"
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </CardContent>
                          </Card>
                        </Grid>

                        {/* GIG_LIMIT_TIME_LIMIT */}
                        <Grid item xs={12} md={4}>
                          <Card
                            sx={{
                              bgcolor: "background.paper",
                              color: "text.primary",
                            }}
                          >
                            <CardContent>
                              <Box display="flex" alignItems="center" mb={2}>
                                <CheckCircle
                                  sx={{ mr: 1, color: "primary.main" }}
                                />
                                <Typography
                                  variant="subtitle1"
                                  fontWeight="bold"
                                >
                                  حجم محدود - زمان محدود
                                </Typography>
                              </Box>
                              <Typography variant="body2" sx={{ mb: 2 }}>
                                <strong>فرمول:</strong> قیمت پایه × تعداد
                                گیگابایت انتخاب شده
                              </Typography>
                              <Box
                                sx={{
                                  bgcolor: "grey.100",
                                  p: 2,
                                  borderRadius: 1,
                                  mb: 2,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  fontFamily="monospace"
                                >
                                  مثال: اگر قیمت پایه 800 تومان باشد و کاربر 30
                                  گیگابایت انتخاب کند:
                                  <br />
                                  800 × 30 = 24,000 تومان
                                </Typography>
                              </Box>
                              <Chip
                                label="قیمت در هر گیگابایت"
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>

                      <Divider sx={{ my: 2 }} />

                      <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          <strong>نکته مهم:</strong> قیمت‌های وارد شده در بالا،
                          قیمت پایه برای هر واحد (گیگابایت یا ماه) هستند. قیمت
                          نهایی که کاربر باید پرداخت کند، بر اساس انتخاب‌هایش
                          محاسبه می‌شود.
                        </Typography>
                      </Alert>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Save Button */}
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      startIcon={
                        <>
                          <Save className="icon-btn" />
                        </>
                      }
                      disabled={updating}
                    >
                      {updating ? "در حال ذخیره..." : "ذخیره تنظیمات"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Paper>
    </Box>
  );
};
